/**
 * The quick-create request — what the drawer is being asked to create, and for
 * which record.
 *
 * Same module-store shape as `app/drawerContext.ts` and `app/paletteScope.ts`,
 * and for the same reason: the surface that INVOKES a create (a related panel, a
 * band's chip, a ghost row) and the surface that HOSTS the form (the shell's
 * right drawer) must stay decoupled. A list three levels deep inside a grid
 * cannot reach into the shell, and should not have to.
 *
 * ── Why the drawer and not another dialog ──────────────────────────────────
 * A band is horizontally squeezed — that is the price of showing a child list
 * inside its parent's row — and a create form is the one thing that needs room.
 * The drawer is already the Elevate-sidepane pattern at shell level
 * (`ShellDrawer.tsx`), it already has the width, and it already survives the
 * page underneath changing. Stacking a modal over a modal to add a contact is
 * what this avoids.
 *
 * ── Why `mode` lives here and not in the panel ─────────────────────────────
 * `quick` is the registered subset (`quickCreateFields` — required + summary,
 * capped at six); `full` is every writable field. The invoker can ASK for full
 * directly, and ⌘K promotes quick → full without unmounting, so the draft the
 * user has already typed survives the promotion. Keeping the mode in the
 * request rather than in component state is what makes that survival free.
 */
import { useSyncExternalStore } from 'react'
import type { RelationSlot } from './relations'

export interface QuickCreateRequest {
  slot: RelationSlot
  /** the record being created INTO — the self end of the slot */
  recordId: string
  /** how to name the parent in the header ("Add a person to Verhoeven Industrie") */
  recordLabel?: string
  /** `quick` = the registered subset; `full` = every writable field */
  mode: 'quick' | 'full'
  /** seeded values (a ghost row carries what was already typed) */
  initial?: Record<string, unknown>
}

let current: QuickCreateRequest | null = null
const subs = new Set<() => void>()
const notify = () => subs.forEach((f) => f())

export function useQuickCreate(): QuickCreateRequest | null {
  return useSyncExternalStore(
    (cb) => { subs.add(cb); return () => { subs.delete(cb) } },
    () => current,
    () => current,
  )
}

/** Ask the drawer to create into `slot` from `recordId`. */
export function openQuickCreate(req: Omit<QuickCreateRequest, 'mode'> & { mode?: 'quick' | 'full' }) {
  current = { mode: 'quick', ...req }
  notify()
  openSubs.forEach((f) => f())
}

export function closeQuickCreate() {
  current = null
  notify()
}

/**
 * Promote to the full form, keeping the draft.
 *
 * Idempotent on purpose: ⌘K is also the palette's own shortcut, so it can arrive
 * while already full, and a promotion that reset the mode would throw away what
 * the user typed to get here.
 */
export function expandQuickCreate() {
  if (!current || current.mode === 'full') return
  current = { ...current, mode: 'full' }
  notify()
}

/** Carry the in-progress draft across a re-open, so a promotion loses nothing. */
export function seedQuickCreate(values: Record<string, unknown>) {
  if (!current) return
  current = { ...current, initial: values }
  // deliberately NOT notified: this is a write-behind so the panel's own state
  // survives a mode change, not a reason to re-render the panel mid-typing.
}

/* ── the shell subscribes here so opening a create also OPENS the drawer ── */
const openSubs = new Set<() => void>()

/** ShellDrawer registers here; a create request pops the drawer to its tenant. */
export function onQuickCreateOpen(cb: () => void): () => void {
  openSubs.add(cb)
  return () => { openSubs.delete(cb) }
}
